import { useFormikFieldState } from "@components/forms/FormikField/FormikField";
import { Switch } from "@design-system/Switch/Switch";
import { ChoiceFieldLabel, ChoiceFieldShell } from "./ChoiceFieldShell";
import type { FieldPath } from "./fieldPath.types";
import type { KitChoiceFieldProps } from "./kitFieldProps.types";

/** Paths of `Values` that hold a plain `boolean`. */
export type SwitchFieldPath<Values> = FieldPath<Values, boolean, boolean>;

export type SwitchFieldProps<Name extends string> = KitChoiceFieldProps<
    Name,
    HTMLElement,
    boolean
>;

/**
 * Switch bound to a typed `boolean` Formik path. The label sits beside the
 * track (`labelHidden` keeps it for screen readers only; `aria-labelledby`
 * replaces it); the error shows below after blur or submit. Values that are
 * not plain booleans (timestamps, `boolean | null`) stay on `FormikField`.
 */
export const SwitchField = <
    Values,
    Name extends SwitchFieldPath<Values> = SwitchFieldPath<Values>,
>({
    name,
    label,
    labelHidden,
    "aria-labelledby": ariaLabelledby,
    optional,
    description,
    className,
    showErrorMessage,
    ref,
    disabled,
    readOnly,
    required,
    onValueChange,
}: SwitchFieldProps<Name>) => {
    const { field, helpers, invalid, error, rootRef } =
        useFormikFieldState<boolean>({
            name,
            showErrorMessage,
        });

    return (
        <ChoiceFieldShell
            ref={rootRef}
            invalid={invalid}
            error={error}
            description={description}
            className={className}
        >
            <Switch
                ref={ref}
                name={name}
                checked={field.value}
                onCheckedChange={(checked) => {
                    void helpers.setValue(checked);
                    onValueChange?.(checked);
                }}
                onBlur={() => void helpers.setTouched(true)}
                invalid={invalid}
                disabled={disabled}
                readOnly={readOnly}
                required={required}
                label={
                    ariaLabelledby === undefined ? (
                        <ChoiceFieldLabel label={label} optional={optional} />
                    ) : undefined
                }
                labelHidden={labelHidden}
                aria-labelledby={ariaLabelledby}
            />
        </ChoiceFieldShell>
    );
};
